import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from "react";
import { api, getToken, setToken, clearToken, ApiError } from "./api.js";

interface Me {
  id: string;
  email: string;
  display_name: string;
  role?: string;
  isAdmin?: boolean;
}

interface AuthCtx {
  me: Me | null;
  loading: boolean;
  login: (email: string, password: string, totp?: string) => Promise<void>;
  register: (email: string, password: string, displayName: string) => Promise<void>;
  logout: () => void;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!getToken()) {
      setLoading(false);
      return;
    }
    api<Me>("/v1/auth/me")
      .then(setMe)
      .catch(() => clearToken())
      .finally(() => setLoading(false));
  }, []);

  // Keep the session alive silently. The login token is long-lived, but we still
  // rotate it: on each app open (slides the 30-day window forward), on window
  // focus (throttled), and on a slow interval for a viewer that's left open for
  // days. Without this the operator gets logged out mid-session and has to sign
  // in again even though the remote machine is sitting there reachable.
  useEffect(() => {
    if (!me) return;
    let stopped = false;
    let lastRefresh = Date.now();

    const doRefresh = async (): Promise<void> => {
      if (stopped || !getToken()) return;
      try {
        const { token } = await api<{ token: string }>("/v1/auth/refresh", {
          method: "POST"
        });
        setToken(token);
        lastRefresh = Date.now();
      } catch (err) {
        // Only a genuine auth failure ends the session — a transient network
        // blip must NOT log the user out. api() already clears the token on a
        // real 401; mirror that for the 403 a disabled account returns.
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          clearToken();
          setMe(null);
        }
      }
    };

    void doRefresh();
    const iv = window.setInterval(() => void doRefresh(), 6 * 60 * 60 * 1000);
    const onFocus = (): void => {
      if (Date.now() - lastRefresh > 60 * 60 * 1000) void doRefresh();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);

    return () => {
      stopped = true;
      window.clearInterval(iv);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [me?.id]);

  const value = useMemo<AuthCtx>(
    () => ({
      me,
      loading,
      async login(email, password, totp) {
        const res = await api<{ token: string }>("/v1/auth/login", {
          method: "POST",
          body: JSON.stringify({ email, password, totp })
        });
        setToken(res.token);
        const user = await api<Me>("/v1/auth/me");
        setMe(user);
      },
      async register(email, password, displayName) {
        await api<{ id: string }>("/v1/auth/register", {
          method: "POST",
          body: JSON.stringify({ email, password, displayName })
        });
        // Auto-login after register.
        const res = await api<{ token: string }>("/v1/auth/login", {
          method: "POST",
          body: JSON.stringify({ email, password })
        });
        setToken(res.token);
        const user = await api<Me>("/v1/auth/me");
        setMe(user);
      },
      logout() {
        clearToken();
        setMe(null);
      }
    }),
    [me, loading]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth must be used inside AuthProvider");
  return v;
}
